/**
 * Import API wrappers (BUILD_SPEC.md §6.5, §11.1).
 *
 * Two phases:
 *   • `previewImport` uploads the file as multipart and returns the
 *     parsed snapshot + auto-detected column mapping;
 *   • `commitImport` confirms the mapping and inserts the rows.
 *
 * The `fileId` the SPA passes to commit comes straight from the
 * preview response — it's a server-issued UUID with a 10-minute Redis
 * TTL. If the user takes longer to confirm than the TTL allows, the
 * server returns 409 PREVIEW_EXPIRED and the SPA re-uploads.
 */

import { api } from "./client";

export type ImportField =
  | "requestNumber"
  | "year"
  | "region"
  | "serviceType"
  | "followUpPhone"
  | "ownerName"
  | "notes";

export type ColumnMapping = Record<ImportField, string | null>;

export interface PreviewResponse {
  fileId: string;
  fileName: string;
  fileSize: number;
  totalRows: number;
  headerRow: string[];
  firstFiveRows: string[][];
  detectedColumns: ColumnMapping;
  warnings: string[];
  ttlSeconds: number;
}

export interface CommitResponse {
  imported: number;
  skipped: number;
  errors: Array<{ row: number; message: string }>;
}

/** Posts the user-selected file to the preview endpoint. The browser
 *  composes the multipart body — we just hand the `File` to FormData. */
export async function previewImport(file: File): Promise<PreviewResponse> {
  const form = new FormData();
  form.append("file", file);
  const res = await api.post<PreviewResponse>("/import/transactions/preview", form, {
    // Let the browser set the multipart boundary; explicitly clearing
    // the Content-Type lets axios omit its default JSON header.
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function commitImport(
  fileId: string,
  columnMapping: ColumnMapping,
): Promise<CommitResponse> {
  const res = await api.post<CommitResponse>("/import/transactions/commit", {
    fileId,
    columnMapping,
  });
  return res.data;
}
